import {
	FieldMappingSourceType,
	MSDYNAMICS_OPPORTUNITY_RESERVED_FIELD_KEYS,
	MsdynamicsEntityType,
	MsdynamicsField,
	MsdynamicsFieldMapping,
	MsdynamicsSourceField,
	MsdynamicsSourceFieldsResponse,
} from "../schema/dynamics";

import type { NewDynamicsMapping } from "./DynamicsMappingRow";

/** One tab of the mapping modal — one Dataverse entity each. */
export enum DynamicsMappingTab {
	CONTACTS = "CONTACTS",
	ACCOUNTS = "ACCOUNTS",
	LEADS = "LEADS",
	OPPORTUNITIES = "OPPORTUNITIES",
	CAMPAIGN_RESPONSES = "CAMPAIGN_RESPONSES",
	CAMPAIGNS = "CAMPAIGNS",
	MARKETING_LISTS = "MARKETING_LISTS",
}

export const DYNAMICS_TAB_ENTITY: Record<DynamicsMappingTab, MsdynamicsEntityType> = {
	[DynamicsMappingTab.CONTACTS]: MsdynamicsEntityType.CONTACT,
	[DynamicsMappingTab.ACCOUNTS]: MsdynamicsEntityType.ACCOUNT,
	[DynamicsMappingTab.LEADS]: MsdynamicsEntityType.LEAD,
	[DynamicsMappingTab.OPPORTUNITIES]: MsdynamicsEntityType.OPPORTUNITY,
	[DynamicsMappingTab.CAMPAIGN_RESPONSES]: MsdynamicsEntityType.CAMPAIGN_RESPONSE,
	[DynamicsMappingTab.CAMPAIGNS]: MsdynamicsEntityType.CAMPAIGN,
	[DynamicsMappingTab.MARKETING_LISTS]: MsdynamicsEntityType.MARKETING_LIST,
};

/**
 * The attributes a tab may target. Opportunity mappings hide the attributes the deal config
 * and the relationship linking own — `POST field-mappings` rejects them with a 400.
 */
export const targetFieldsForTab = (
	tab: DynamicsMappingTab,
	fields: MsdynamicsField[]
): MsdynamicsField[] =>
	tab === DynamicsMappingTab.OPPORTUNITIES
		? fields.filter((field) => !MSDYNAMICS_OPPORTUNITY_RESERVED_FIELD_KEYS.includes(field.name))
		: fields;

/**
 * The Signalist-side fields a tab may read from. `source-fields` is keyed per Dataverse
 * entity, so each tab has a direct key; `contactFields` is the fallback for the two the
 * backend marks optional.
 */
export const sourceFieldsForTab = (
	tab: DynamicsMappingTab,
	sourceFields: MsdynamicsSourceFieldsResponse | undefined
): MsdynamicsSourceField[] => {
	switch (tab) {
		case DynamicsMappingTab.ACCOUNTS:
			return sourceFields?.accountFields ?? [];
		case DynamicsMappingTab.LEADS:
			return sourceFields?.leadFields ?? sourceFields?.contactFields ?? [];
		case DynamicsMappingTab.OPPORTUNITIES:
			return sourceFields?.opportunityFields ?? sourceFields?.contactFields ?? [];
		case DynamicsMappingTab.CAMPAIGN_RESPONSES:
			return sourceFields?.campaignResponseFields ?? [];
		case DynamicsMappingTab.CAMPAIGNS:
			return sourceFields?.campaignFields ?? [];
		case DynamicsMappingTab.MARKETING_LISTS:
			return sourceFields?.marketingListFields ?? [];
		default:
			return sourceFields?.contactFields ?? [];
	}
};

/** Dataverse target types that take a LOOKUP value map (the API's `valueMapTargetFor`). */
const LOOKUP_VALUE_MAP_TARGET_TYPES = new Set(["lookup", "customer"]);

const normalizedType = (field: MsdynamicsField): string =>
	String(field.type ?? "")
		.toLowerCase()
		.replace(/type$/, "");

/**
 * Whether a new mapping row may offer this target. A relationship attribute (Lookup, Customer,
 * Owner, PartyList) only accepts an `@odata.bind`, which a value-mapped source writes — and only
 * a Lookup/Customer target takes that map; an Owner/PartyList has no value map at all. Mirrors
 * the API's `relationshipTargetRefusal` in `msdynamics-field-mapping.validation.ts`, so the
 * picker never offers what the save guard refuses (PREC-4599).
 */
export const canOfferTargetField = (
	field: MsdynamicsField,
	source: MsdynamicsSourceField | undefined
): boolean =>
	!field.isRelationship ||
	(source?.hasValueMap === true && LOOKUP_VALUE_MAP_TARGET_TYPES.has(normalizedType(field)));

/** Whether a chosen relationship target cannot be saved from the chosen source. */
export const isRelationshipTargetBlocked = (
	target: MsdynamicsField | undefined,
	source: MsdynamicsSourceField | undefined
): boolean =>
	Boolean(target?.isRelationship) && !canOfferTargetField(target as MsdynamicsField, source);

const isFieldMode = (sourceType?: FieldMappingSourceType): boolean =>
	(sourceType ?? FieldMappingSourceType.FIELD) === FieldMappingSourceType.FIELD;

/**
 * Whether every sync drops this stored mapping: its target is a relationship attribute and its
 * source cannot write the `@odata.bind` that attribute needs. Only a FIELD source with a lookup
 * value map can (a CONSTANT or AI value is always text), so this is the same decision the save
 * guard makes (`relationshipTargetRefusal` in the API) applied to a row saved before PREC-4599.
 * `false` while the target's metadata is unknown, since the sync then cannot be predicted.
 */
export const isMappingDroppedBySync = (
	mapping: Pick<MsdynamicsFieldMapping, "sourceType" | "sourceField">,
	target: MsdynamicsField | undefined,
	sourceFields: MsdynamicsSourceField[]
): boolean => {
	const source = isFieldMode(mapping.sourceType)
		? sourceFields.find((field) => field.name === mapping.sourceField)
		: undefined;

	return isRelationshipTargetBlocked(target, source);
};

/** Source paths already mapped on this tab, so the picker cannot offer one twice. */
export const collectUsedSourceFields = (
	saved: MsdynamicsFieldMapping[] | undefined,
	unsaved: NewDynamicsMapping[]
): string[] => [
	...(saved ?? [])
		.filter((mapping) => isFieldMode(mapping.sourceType) && mapping.sourceField)
		.map((mapping) => mapping.sourceField),
	...unsaved
		.filter((mapping) => isFieldMode(mapping.sourceType) && mapping.sourceField)
		.map((mapping) => mapping.sourceField),
];

/**
 * Target attributes already mapped on this tab, per source type — the same attribute may be
 * fed by a FIELD and by an AI mapping, so the exclusion is per bucket rather than global.
 */
export const collectUsedTargetFields = (
	saved: MsdynamicsFieldMapping[] | undefined,
	unsaved: NewDynamicsMapping[]
): Record<FieldMappingSourceType, string[]> => {
	const byType: Record<FieldMappingSourceType, string[]> = {
		[FieldMappingSourceType.FIELD]: [],
		[FieldMappingSourceType.CONSTANT]: [],
		[FieldMappingSourceType.AI]: [],
	};

	(saved ?? [])
		.filter((mapping) => mapping.msdynamicsField)
		.forEach((mapping) =>
			byType[mapping.sourceType ?? FieldMappingSourceType.FIELD].push(mapping.msdynamicsField)
		);

	unsaved
		.filter((mapping) => mapping.msdynamicsField)
		.forEach((mapping) => byType[mapping.sourceType].push(mapping.msdynamicsField));

	return byType;
};
